import { CONSTANTS, ACCESS_TYPES, ACCESS_TYPE_LABELS } from "./constants";

const MAX_GAP_MINUTES = 15;

/**
 * Check if an event is a swimming event based on title, color, etc.
 * @param {Object} event - Event object from API
 * @returns {boolean} - Whether it's a swimming event
 */
export function isSwimmingEvent(event) {
  const title = event.title || "";
  const lower = title.toLowerCase();

  // Always include closed events (Busy, Pool Party, etc.)
  if (matchesAny(title, CONSTANTS.ACCESS_CLOSED)) {
    return true;
  }

  // Check backgroundColor first - blue (#0000FF) indicates pool events
  if (event.backgroundColor === CONSTANTS.EVENT_COLORS.POOL) {
    // Exclude non-pool events even if they have blue background
    if (
      CONSTANTS.NON_POOL_KEYWORDS.some((keyword) => lower.includes(keyword))
    ) {
      return false;
    }

    return true;
  }

  // For events without blue background, check for "swim" keyword
  return lower.includes(CONSTANTS.FALLBACK_KEYWORDS.SWIM);
}

/**
 * Check if title matches any of the keywords
 * @param {string} title - Event title
 * @param {Array|string} keywords - Keywords to check
 * @returns {boolean} - Whether there's a match
 */
function matchesAny(title, keywords) {
  const lowerTitle = title.toLowerCase();

  if (Array.isArray(keywords)) {
    return keywords.some((keyword) => lowerTitle.includes(keyword));
  }

  return lowerTitle.includes(keywords);
}

function isPlayPoolOpen(title) {
  const lowerTitle = title.toLowerCase();
  return (
    matchesAny(title, CONSTANTS.FALLBACK_KEYWORDS.PLAY_THERAPY) ||
    (lowerTitle.includes("play") && lowerTitle.includes("open"))
  );
}

// Titles often enumerate which pools are open, e.g.
//   "Recreational Swim - 4 Lanes & Therapy Pool Open"
//   "Recreational Swim - 4 Lanes, Play & Therapy Pools Open"
//   "Public Swim - No Lanes, All Pools Available"
// Returns the enumerated list (lowercase) or null when no enumeration exists.
function getOpenPoolsEnumeration(title) {
  const matches = [
    ...title.toLowerCase().matchAll(/([^-,]+?)\s+pools?\s+(open|available)/g),
  ];
  if (matches.length === 0) return null;
  return matches[matches.length - 1][1].trim();
}

/**
 * Check if the title has a numeric lane count
 * @param {string} title - Event title
 * @returns {Object} - Object with boolean 'hasLanes' and 'count' properties
 */
function getLaneCount(title) {
  const lanesMatch = title.match(/(\d+)\s+lane/i);

  return {
    hasLanes: lanesMatch !== null,
    count: lanesMatch ? parseInt(lanesMatch[1]) : 0,
  };
}

/**
 * Analyze an event to determine its properties (lanes, kids, restrictions)
 * @param {Object} event - Event object
 * @returns {Object} - Analysis of the event
 */
export function analyzeEvent(event) {
  const title = event.title;
  const lowerTitle = title.toLowerCase();

  // Check if this is a closed event
  if (matchesAny(title, CONSTANTS.ACCESS_CLOSED)) {
    return {
      lanes: false,
      kids: false,
      membersOnly: false,
      restrictedAccess: false,
      closedToPublic: true,
      type: ACCESS_TYPE_LABELS[ACCESS_TYPES.CLOSED],
      details: { lanes: 0 },
    };
  }

  // Check for lane availability - priority order matters
  let lanes = false;
  if (matchesAny(title, CONSTANTS.LANES_ALWAYS_CLOSED)) {
    lanes = false;
  } else if (matchesAny(title, CONSTANTS.LANES_ALWAYS_OPEN)) {
    lanes = true;
  } else {
    // If not in either list, check for lane mentions
    const laneInfo = getLaneCount(title);
    lanes =
      laneInfo.hasLanes || matchesAny(title, CONSTANTS.FALLBACK_KEYWORDS.LANE);
  }

  // Check for kids pool availability - priority order matters
  let kids = false;
  if (matchesAny(title, CONSTANTS.KIDS_ALWAYS_CLOSED)) {
    kids = false;
  } else {
    // An explicit enumeration of open pools in the title is the strongest signal:
    // trust it over keyword fallbacks like "Recreational Swim" being in KIDS_ALWAYS_OPEN.
    const enumeration = getOpenPoolsEnumeration(title);
    if (enumeration !== null) {
      kids = /\bplay\b/.test(enumeration) || /\ball\b/.test(enumeration);
    } else if (matchesAny(title, CONSTANTS.KIDS_ALWAYS_OPEN)) {
      kids = true;
    } else if (isPlayPoolOpen(title)) {
      kids = true;
    } else if (
      matchesAny(title, CONSTANTS.FALLBACK_KEYWORDS.RECREATIONAL) &&
      !lowerTitle.includes("no play")
    ) {
      kids = true;
    } else {
      kids = matchesAny(title, CONSTANTS.FALLBACK_KEYWORDS.FAMILY);
    }
  }

  // Access restrictions
  const membersOnly = matchesAny(title, CONSTANTS.ACCESS_MEMBERS);
  const isSensory = matchesAny(title, CONSTANTS.ACCESS_SENSORY);
  const restrictedAccess = matchesAny(title, [
    ...CONSTANTS.ACCESS_WOMENS,
    ...CONSTANTS.ACCESS_SENIORS,
    ...CONSTANTS.ACCESS_SENSORY,
  ]);

  // Determine restriction type
  let restrictionType = ACCESS_TYPE_LABELS[ACCESS_TYPES.PUBLIC];
  if (matchesAny(title, CONSTANTS.ACCESS_WOMENS)) {
    restrictionType = ACCESS_TYPE_LABELS[ACCESS_TYPES.WOMENS];
  } else if (matchesAny(title, CONSTANTS.ACCESS_SENIORS)) {
    restrictionType = ACCESS_TYPE_LABELS[ACCESS_TYPES.SENIORS];
  } else if (isSensory) {
    restrictionType = ACCESS_TYPE_LABELS[ACCESS_TYPES.SENSORY];
  } else if (membersOnly) {
    restrictionType = ACCESS_TYPE_LABELS[ACCESS_TYPES.MEMBERS];
  }

  // Get lane count
  const laneInfo = getLaneCount(title);

  return {
    lanes,
    kids,
    membersOnly,
    restrictedAccess,
    isSensory,
    type: restrictionType,
    details: {
      lanes: laneInfo.count,
    },
  };
}

/**
 * Find the current status and next time change for a particular feature (lanes or kids)
 * @param {Array} events - List of events
 * @param {Date} now - Current date/time
 * @param {string} type - Type to look for ("lanes" or "kids")
 * @returns {Object} - Information about the current status and next time change
 */
export function findFeatureStatus(events, now, type) {
  // Get today's events
  const todayStr = now.toDateString();
  const todayEvents = events.filter(
    (event) => new Date(event.start).toDateString() === todayStr,
  );

  // Sort by start time
  todayEvents.sort((a, b) => new Date(a.start) - new Date(b.start));

  // Find currently active and next events
  let currentEvent = null;
  let featureEndTime = null;
  let nextFeatureStartTime = null;
  let restrictedAccess = false;
  let membersOnly = false;
  let restrictionType = null;

  // First, check if the feature is currently active
  for (const event of todayEvents) {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);

    // Check if this event is happening now
    if (eventStart <= now && eventEnd > now) {
      const analysis = analyzeEvent(event);

      // Check if this event has the feature we're looking for
      const hasFeature =
        (type === "lanes" && analysis.lanes) ||
        (type === "kids" && analysis.kids);

      if (hasFeature) {
        currentEvent = event;
        restrictedAccess = analysis.restrictedAccess;
        membersOnly = analysis.membersOnly;
        restrictionType = analysis.type;

        // For special events, just use the event's end time directly instead of looking for consecutive events
        if (restrictedAccess || membersOnly) {
          return {
            isActive: true,
            endTime: eventEnd,
            details: analysis.details || {},
            restrictedAccess,
            membersOnly,
            restrictionType,
            specialEvent: true,
            eventTitle: event.title,
          };
        }

        break; // Found a current event with the feature
      }
    }
  }

  // If feature is currently active (and not a special event which was returned above),
  // find when it will end (looking at consecutive events)
  if (currentEvent) {
    // Start from when the current event ends
    featureEndTime = new Date(currentEvent.end);

    // Look for consecutive events that have the feature
    let currentEndTime = featureEndTime;

    // Create a sorted copy of the events for scanning consecutive ones
    const sortedEvents = [...todayEvents].sort(
      (a, b) => new Date(a.start) - new Date(b.start),
    );

    // Look for events starting after our current event
    for (const event of sortedEvents) {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      // Skip events that aren't after our current event
      if (
        eventStart <= now ||
        new Date(event.start) <= new Date(currentEvent.start)
      ) {
        continue;
      }

      const analysis = analyzeEvent(event);

      // Check if this event has the feature we're looking for
      const hasFeature =
        (type === "lanes" && analysis.lanes) ||
        (type === "kids" && analysis.kids);

      // IMPROVED GAP HANDLING: Bridge reasonable gaps between events with the same feature
      const gapMinutes = (eventStart - currentEndTime) / (1000 * 60);

      if (gapMinutes >= 0 && gapMinutes <= MAX_GAP_MINUTES && hasFeature) {
        // Stop extending if we encounter a special event (members only, restricted access)
        if (analysis.restrictedAccess || analysis.membersOnly) {
          break;
        }

        // Extend the continuous availability time through the gap
        featureEndTime = eventEnd;
        currentEndTime = eventEnd;
      } else if (eventStart > currentEndTime && !hasFeature) {
        // We've found an event without the feature - this is the end of continuous availability
        break;
      } else if (gapMinutes > MAX_GAP_MINUTES) {
        // Gap is too large to bridge
        break;
      }
    }

    // If feature is currently active, return its status
    return {
      isActive: true,
      endTime: featureEndTime,
      details: analyzeEvent(currentEvent).details || {},
      restrictedAccess,
      membersOnly,
      restrictionType,
      specialEvent: false,
      eventTitle: currentEvent.title,
    };
  }

  // If feature is not currently active, find when it will next be available
  if (!currentEvent) {
    for (const event of todayEvents) {
      const eventStart = new Date(event.start);

      // Only look at future events
      if (eventStart > now) {
        const analysis = analyzeEvent(event);
        const hasFeature =
          (type === "lanes" && analysis.lanes) ||
          (type === "kids" && analysis.kids);

        if (hasFeature) {
          nextFeatureStartTime = eventStart;
          restrictedAccess = analysis.restrictedAccess;
          membersOnly = analysis.membersOnly;
          restrictionType = analysis.type;
          break; // Found the next event with the feature
        }
      }
    }

    // If we found a future event with the feature
    if (nextFeatureStartTime) {
      return {
        isActive: false,
        inGap: true,
        nextStartTime: nextFeatureStartTime,
        restrictedAccess,
        membersOnly,
        restrictionType: restrictionType,
      };
    }
  }

  // No more events with this feature today
  return {
    isActive: false,
    inGap: false,
  };
}
